import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ShortlistProvider } from "@/context/ShortlistContext";
import { Layout } from "@/components/layout/Layout";
import HomePage from "@/pages/HomePage";
import BuyCarsPage from "@/pages/BuyCarsPage";
import VehicleDetailPage from "@/pages/VehicleDetailPage";
import SellCarPage from "@/pages/SellCarPage";
import LoansPage from "@/pages/LoansPage";
import AboutPage from "@/pages/AboutPage";
import ContactPage from "@/pages/ContactPage";
import ReviewsPage from "@/pages/ReviewsPage";
import FAQPage from "@/pages/FAQPage";
import CertifiedPage from "@/pages/CertifiedPage";
import BlogPage from "@/pages/BlogPage";
import ShortlistPage from "@/pages/ShortlistPage";
import AdminLoginPage from "@/pages/AdminLoginPage";
import { AdminLayout } from "@/components/admin/AdminLayout";
import DashboardPage from "@/pages/admin/DashboardPage";
import InventoryPage from "@/pages/admin/InventoryPage";
import VehicleFormPage from "@/pages/admin/VehicleFormPage";
import LeadsPage from "@/pages/admin/LeadsPage";
import CustomersPage from "@/pages/admin/CustomersPage";
import MediaPage from "@/pages/admin/MediaPage";
import ReviewsAdminPage from "@/pages/admin/ReviewsAdminPage";
import InstagramAdminPage from "@/pages/admin/InstagramAdminPage";
import BlogAdminPage from "@/pages/admin/BlogAdminPage";
import SettingsAdminPage from "@/pages/admin/SettingsAdminPage";
import "@/App.css";

const Protected = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-10 text-center text-gray-500">Loading…</div>;
  if (!user || user.role !== "admin") return <Navigate to="/admin/login" state={{ from: window.location.pathname }} replace />;
  return children;
};

const PageTitle = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    document.title = pathname === "/" ? "Carz Swag | Buy & Sell Used Cars in Delhi" : "Carz Swag | Buy & Sell Used Cars";
  }, [pathname]);
  return null;
};

function App() {
  return (
    <AuthProvider>
      <ShortlistProvider>
        <BrowserRouter>
          <Toaster richColors position="top-center" />
          <PageTitle />
          <Routes>
            <Route path="/" element={<Layout />}>
              <Route index element={<HomePage />} />
              <Route path="buy" element={<BuyCarsPage />} />
              <Route path="car/:id" element={<VehicleDetailPage />} />
              <Route path="sell" element={<SellCarPage />} />
              <Route path="loans" element={<LoansPage />} />
              <Route path="about" element={<AboutPage />} />
              <Route path="contact" element={<ContactPage />} />
              <Route path="reviews" element={<ReviewsPage />} />
              <Route path="faq" element={<FAQPage />} />
              <Route path="certified" element={<CertifiedPage />} />
              <Route path="blog" element={<BlogPage />} />
              <Route path="shortlist" element={<ShortlistPage />} />
            </Route>
            <Route path="/admin/login" element={<AdminLoginPage />} />
            <Route path="/admin" element={<Protected><AdminLayout /></Protected>}>
              <Route index element={<DashboardPage />} />
              <Route path="inventory" element={<InventoryPage />} />
              <Route path="inventory/new" element={<VehicleFormPage />} />
              <Route path="inventory/:id" element={<VehicleFormPage />} />
              <Route path="leads" element={<LeadsPage />} />
              <Route path="sell-requests" element={<LeadsPage sourceFilter="sell_car" />} />
              <Route path="test-drives" element={<LeadsPage sourceFilter="test_drive" />} />
              <Route path="customers" element={<CustomersPage />} />
              <Route path="reviews" element={<ReviewsAdminPage />} />
              <Route path="instagram" element={<InstagramAdminPage />} />
              <Route path="blog" element={<BlogAdminPage />} />
              <Route path="media" element={<MediaPage />} />
              <Route path="settings" element={<SettingsAdminPage />} />
              <Route path="*" element={<Navigate to="/admin" replace />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </ShortlistProvider>
    </AuthProvider>
  );
}

export default App;
